import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable10() {
  const pdfRef = useRef();

  const data = [
    { age_group_label: "14–18", female_emp: 0 },
    { age_group_label: "19–21", female_emp: 12 },
    { age_group_label: "22–24", female_emp: 17 },
    { age_group_label: "25–34", female_emp: 44 },
    { age_group_label: "35–44", female_emp: 58 },
    { age_group_label: "45–54", female_emp: 56 },
    { age_group_label: "55–64", female_emp: 26 },
    { age_group_label: "65–99", female_emp: 18 },
  ];

  // Sort by logical age order (already sorted but ensuring correctness)
  const sortedData = data;

  // Chart Data
  const chartData = {
    labels: sortedData.map((d) => d.age_group_label),
    datasets: [
      {
        label: "Female Employment (2024)",
        data: sortedData.map((d) => d.female_emp),
        backgroundColor: "rgba(236, 72, 153, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "👩‍🏭 Female Employment by Age Group in Utah Mining (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Female Employees" },
        beginAtZero: true,
      },
    },
  };

  // Insights
  const total = sortedData.reduce((sum, d) => sum + d.female_emp, 0);
  const top = sortedData.reduce((max, d) =>
    d.female_emp > max.female_emp ? d : max
  );
  const lowest = sortedData.reduce((min, d) =>
    d.female_emp < min.female_emp ? d : min
  );

  // PDF Export
  const downloadPDF = async () => {
    const input = pdfRef.current;
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Header
    pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
    pdf.setFontSize(26);
    pdf.text("UTahData.org", 95, 22);
    pdf.line(10, 35, 200, 35);

    // Content
    pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

    // Footer
    const footerY = 275;
    pdf.line(10, footerY - 5, 200, footerY - 5);
    pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
    pdf.setFontSize(10);
    pdf.text("Built by", 55, footerY + 10);
    pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
    pdf.text("using", 112, footerY + 10);
    pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
    pdf.text("20250916.v3.198", 148, footerY + 10);
    pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 10, 295);
    pdf.text(`From: ${window.location.href}`, 100, 295);

    pdf.save("utah_female_employment_by_agegroup.pdf");
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-pink-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Female Employment by Age Group in Utah Mining (2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Female Employees
            </p>
            <p className="text-xl font-bold text-green-800">{total}</p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Representation
            </p>
            <p className="text-lg font-bold text-pink-800">
              {top.age_group_label} ({top.female_emp})
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Lowest Representation
            </p>
            <p className="text-lg font-bold text-yellow-800">
              {lowest.age_group_label} ({lowest.female_emp})
            </p>
          </div>
        </div>

        {/* Chart + Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Female Employment</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.age_group_label === top.age_group_label
                        ? "bg-green-100 font-semibold"
                        : row.age_group_label === lowest.age_group_label
                          ? "bg-yellow-100 font-semibold"
                          : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.age_group_label}</td>
                    <td className="p-2 text-pink-700">{row.female_emp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4 flex-grow h-[400px]">
            <Bar data={chartData} options={options} />
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-700 mt-4">
          👩‍🏭 The age group <b>{top.age_group_label}</b> has the highest female
          representation, while <b>{lowest.age_group_label}</b> shows the
          lowest. Most female mining employees are between <b>25–54 years</b> of
          age.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
